import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Brand-aligned select dropdown. Replaces the native <select> across
// the dashboard — the macOS native menu (dark popover, system fonts)
// clashes with everything else mag's wearing.
//
// API mirrors the native element where it can: `value`, `onChange`,
// `options`, `disabled`. Options carry an optional `hint` line for
// when the choice needs a one-line explanation (cadence picker,
// asset kind, etc.). For the simple labels-only case the hint is
// omitted and the row renders identical to a native option.
//
// Behavior:
//   - click trigger → opens, focuses the active option
//   - click outside → close
//   - ESC → close + return focus to trigger
//   - ↑ / ↓ → move highlight; Enter → pick; Tab leaves the menu open-
//     loop simple for the v1 (no roving tabindex), good enough until
//     a screen-reader-led test pass surfaces a real gap.
//
// The menu renders in a PORTAL (document.body) with fixed positioning
// measured off the trigger, so it escapes any `overflow-hidden` /
// clipping ancestor (e.g. the home onboarding-checklist's animated,
// overflow-hidden step rows were cutting the dropdown + the buttons
// below it). Position re-measures on scroll/resize and flips above the
// trigger when there isn't room below.

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Optional mark rendered before the label, in both the trigger and
   *  the list. Added for the slack channel picker, where a padlock
   *  distinguishes a private channel from a public one — the sigil is
   *  how you tell at a glance who will be able to read what lands
   *  there. Pass a small inline svg sized to the text, not an emoji:
   *  this app draws its own marks (see SlackGlyph, RailGlyph) and a
   *  full-colour emoji reads as a foreign object beside them. */
  icon?: React.ReactNode;
  /** Optional second line, smaller + dimmer. */
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  /** Shown when no value is set. Defaults to "select…" — replace when
   *  the surrounding context needs something more concrete ("pick a
   *  kind", "pick a cadence"). */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** ARIA label fallback when the field isn't wrapped in a <label>. */
  ariaLabel?: string;
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder = 'select…',
  disabled = false,
  className,
  ariaLabel,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(
    Math.max(
      0,
      options.findIndex((o) => o.value === value),
    ),
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Fixed-position box for the portaled menu, measured off the trigger.
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    // Flip above only when below is genuinely cramped and above has more.
    const placeAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    setPos({
      left: r.left,
      width: r.width,
      ...(placeAbove ? { bottom: window.innerHeight - r.top + gap } : { top: r.bottom + gap }),
      maxHeight: Math.max(140, (placeAbove ? spaceAbove : spaceBelow) - gap),
    });
  }, []);

  // Measure on open (useLayoutEffect → before paint, no flicker) and
  // keep it pinned to the trigger as the page scrolls / resizes. The
  // capture-phase scroll listener catches ancestor scroll containers,
  // not just the window.
  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  const activeOption = options.find((o) => o.value === value);

  // Re-sync highlight whenever the menu opens to the current value.
  useEffect(() => {
    if (open) {
      const i = options.findIndex((o) => o.value === value);
      setHighlight(i >= 0 ? i : 0);
    }
  }, [open, value, options]);

  // Click-outside close. mousedown beats click so picking an option
  // inside the menu still fires its onClick (which closes too).
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => nextEnabledIndex(options, i, +1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => nextEnabledIndex(options, i, -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt && !opt.disabled) {
        onChange(opt.value);
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
  };

  return (
    <div className={`relative ${className ?? ''}`}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        // Match .field input: 15px, py-3 (12px) / px-3.5 (14px), 1.5px
        // border, 10px radius. Focus / open state mirrors the input
        // focus: ink border + 3px plum shadow + 1px nudge.
        className={`flex w-full items-center justify-between gap-2 rounded-[10px] border-[1.5px] bg-white px-3.5 py-3 text-left text-[15px] transition disabled:cursor-not-allowed disabled:opacity-50 ${
          open
            ? '-translate-x-px -translate-y-px border-[var(--ink)] shadow-[3px_3px_0_var(--plum)]'
            : 'border-[var(--line-strong)] hover:border-[var(--ink)]'
        }`}
      >
        <span
          className={`inline-flex min-w-0 items-center gap-1.5 ${
            activeOption ? 'text-[var(--ink)]' : 'text-[var(--ink)]/45'
          }`}
        >
          {activeOption?.icon ?? null}
          <span className="truncate">{activeOption?.label ?? placeholder}</span>
        </span>
        <Chevron open={open} />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              id={listboxId}
              role="listbox"
              style={{
                position: 'fixed',
                left: pos.left,
                width: pos.width,
                ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
              }}
              className="z-50 overflow-hidden rounded-lg border-[1.5px] border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--plum)]"
            >
              <ul className="overflow-y-auto py-1" style={{ maxHeight: pos.maxHeight }}>
                {options.map((opt, i) => {
                  const isActive = opt.value === value;
                  const isHighlighted = i === highlight;
                  return (
                    <li key={opt.value} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        aria-disabled={opt.disabled}
                        disabled={opt.disabled}
                        onMouseEnter={() => !opt.disabled && setHighlight(i)}
                        onClick={() => {
                          if (opt.disabled) return;
                          onChange(opt.value);
                          setOpen(false);
                          triggerRef.current?.focus();
                        }}
                        className={`flex w-full items-start gap-2 px-3 py-2 text-left text-[13.5px] transition disabled:opacity-40 ${
                          isHighlighted && !opt.disabled ? 'bg-[var(--paper)]' : 'bg-white'
                        } ${isActive ? 'font-bold text-[var(--ink)]' : 'text-[var(--ink)]/85'}`}
                      >
                        <span
                          aria-hidden
                          className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                            isActive ? 'bg-[var(--spell)]' : 'bg-[var(--ink)]/15'
                          }`}
                        />
                        <span className="flex min-w-0 flex-col">
                          <span className="inline-flex min-w-0 items-center gap-1.5">
                            {opt.icon ?? null}
                            <span className="truncate">{opt.label}</span>
                          </span>
                          {opt.hint ? (
                            <span className="mt-0.5 text-[11.5px] font-normal text-[var(--ink)]/55">
                              {opt.hint}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
      className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path
        d="M3 5l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function nextEnabledIndex<T extends string>(
  options: ReadonlyArray<SelectOption<T>>,
  current: number,
  step: 1 | -1,
): number {
  const len = options.length;
  if (len === 0) return current;
  let next = current;
  for (let attempt = 0; attempt < len; attempt++) {
    next = (next + step + len) % len;
    if (!options[next]?.disabled) return next;
  }
  return current;
}
